import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, formatEuro, formatDate, formatTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, Check, PhoneCall, Loader2, ChevronDown } from "lucide-react";

const ANSWER_LABELS = {
  has_working_key: "Clé fonctionnelle disponible",
  has_remote: "Clé avec télécommande",
  vehicle_locked: "Véhicule verrouillé",
  has_registration: "Carte grise disponible",
  buttons_response: "Boutons répondent partiellement",
  keys_inside: "Clés dans le véhicule",
};
const ANSWER_VAL = { yes: "Oui", no: "Non", unknown: "Ne sait pas" };

export default function CustomerQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showBooking, setShowBooking] = useState(false);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customer, setCustomer] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  function load() {
    axios.get(`${API}/public/quotes/by-token/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  useEffect(() => {
    if (showBooking && data?.tenant?.slug && Object.keys(slotsByDate).length === 0) {
      axios.get(`${API}/public/availability/${data.tenant.slug}`)
        .then((r) => setSlotsByDate(r.data.slots_by_date || {}))
        .catch(() => toast.error("Impossible de charger les créneaux"));
    }
  }, [showBooking, data, slotsByDate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  if (error === "not_found") return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center">
      <div>
        <h1 className="font-display text-2xl font-bold text-[#16324F]">Devis introuvable</h1>
        <p className="mt-2 text-slate-500">Ce lien n'est plus valide ou le devis n'existe plus.</p>
        <Link to="/" className="mt-4 inline-block text-sm font-semibold text-[#19C3B3]">Retour à l'accueil</Link>
      </div>
    </div>
  );
  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-500">Erreur</div>;

  const { quote, tenant, booking } = data;
  const primary = tenant.primary_color || "#16324F";
  const accent = tenant.accent_color || "#19C3B3";
  const alreadyBooked = Boolean(booking);
  const pdfUrl = `${API}/public/quotes/by-token/${token}/pdf`;
  const isDemo = tenant.slug === "cle-express";

  async function submitBooking() {
    if (!selectedSlot) { toast.error("Choisissez un créneau"); return; }
    if (!customer.first_name || !customer.last_name || !customer.phone || !customer.email) {
      toast.error("Renseignez toutes les coordonnées"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/quotes/by-token/${token}/book`, {
        slot_start: selectedSlot,
        customer,
      });
      toast.success("Réservation confirmée !");
      load();
      setShowBooking(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réservation impossible");
    } finally { setSubmitting(false); }
  }

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${primary}0A 0%, #FBFCFE 40%)` }}>
      <header className="px-4 md:px-8 py-5 flex items-center justify-between max-w-3xl mx-auto">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-extrabold text-white" style={{ background: primary }}>
            {tenant.business_name.charAt(0)}
          </div>
          <div>
            <div className="font-display font-bold text-lg" style={{ color: primary }}>{tenant.business_name}</div>
            <div className="text-xs text-slate-500">Devis en ligne · propulsé par MonTempsGagné</div>
          </div>
        </Link>
        {tenant.phone && !isDemo && (
          <a href={`tel:${tenant.phone.replace(/\s/g, "")}`} className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold" style={{color: primary}}>
            <PhoneCall className="w-4 h-4"/> {tenant.phone}
          </a>
        )}
      </header>

      <main className="px-4 md:px-8 py-4 pb-24 max-w-2xl mx-auto">
        {isDemo && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs font-semibold" style={{ borderColor: `${primary}55`, color: primary, background: `${primary}0A` }} data-testid="demo-env-banner">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }}></span>
            Environnement de démonstration
          </div>
        )}
        {alreadyBooked ? (
          <section className="kf-card p-6 md:p-8" data-testid="booking-confirmation-section">
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center" style={{ background: `${accent}20`, color: accent }}>
                <Check className="w-7 h-7"/>
              </div>
              <div className="mt-4 text-xs font-bold uppercase tracking-widest" style={{color: accent}}>Intervention réservée</div>
              <h1 className="mt-2 font-display text-3xl font-extrabold" style={{color: primary}}>Votre intervention est confirmée</h1>
            </div>
            <dl className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 text-left text-sm">
              <dt className="text-xs text-slate-500 uppercase font-semibold">Référence devis</dt><dd className="font-mono font-semibold text-sm" style={{color: primary}} data-testid="ref-quote">{quote.quote_number}</dd>
              <dt className="text-xs text-slate-500 uppercase font-semibold">Référence réservation</dt><dd className="font-mono font-semibold text-sm" style={{color: primary}} data-testid="ref-booking">{booking.reference}</dd>
              <dt className="text-xs text-slate-500 uppercase font-semibold">Date</dt><dd className="font-semibold">{formatDate(booking.slot_start)}</dd>
              <dt className="text-xs text-slate-500 uppercase font-semibold">Heure</dt><dd className="font-semibold">{formatTime(booking.slot_start)}</dd>
              <dt className="text-xs text-slate-500 uppercase font-semibold">Véhicule</dt><dd className="font-semibold">{quote.vehicle?.brand} {quote.vehicle?.model} {quote.vehicle?.year}</dd>
              <dt className="text-xs text-slate-500 uppercase font-semibold">Prestation</dt><dd className="font-semibold">{quote.service_name}</dd>
              <dt className="text-xs text-slate-500 uppercase font-semibold">Prix</dt><dd className="font-display font-extrabold text-lg" style={{color: primary}}>{formatEuro(quote.price)}</dd>
              <dt className="text-xs text-slate-500 uppercase font-semibold">Lieu</dt><dd className="font-semibold">{quote.location?.city} {quote.location?.postal_code}</dd>
            </dl>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="download-pdf-booked">
                <Button variant="outline" className="rounded-full h-11 border-2" style={{ borderColor: primary, color: primary }}><Download className="w-4 h-4 mr-1.5"/> Télécharger mon devis</Button>
              </a>
            </div>
          </section>
        ) : (
          <section className="kf-card p-6 md:p-8" data-testid="quote-ready-section">
            <div className="text-xs font-bold uppercase tracking-widest" style={{color: accent}}>Votre devis est prêt</div>
            <div className="mt-3 font-display text-2xl font-bold" style={{color: primary}}>
              {quote.vehicle?.brand} {quote.vehicle?.model} · {quote.vehicle?.year}
            </div>
            <div className="text-slate-600">{quote.service_name} — {quote.location?.city}</div>
            <div className="mt-6 font-display font-extrabold text-5xl" style={{color: primary}} data-testid="cq-price">
              {formatEuro(quote.price)} <span className="text-lg font-bold text-slate-400">TTC</span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
              <span>Devis N° <span className="font-mono font-semibold" style={{color: primary}} data-testid="cq-number">{quote.quote_number}</span></span>
              <span>· Émis le {formatDate(quote.issued_at)}</span>
              <span>· Valide jusqu'au <span data-testid="cq-valid">{formatDate(quote.valid_until)}</span></span>
            </div>

            {quote.pricing?.notice && (
              <div className="mt-3 text-xs text-slate-500 italic" data-testid="cq-sim-notice">
                {quote.pricing.notice}{quote.pricing.status_label ? ` — ${quote.pricing.status_label}` : ""}
              </div>
            )}

            {quote.applied_rules?.length > 0 && (
              <details className="mt-4 text-sm text-slate-600">
                <summary className="cursor-pointer font-semibold flex items-center gap-1"><ChevronDown className="w-4 h-4"/> Détail du calcul</summary>
                <ul className="mt-2 space-y-1 pl-5">
                  <li>Prix de base : {formatEuro(quote.base_price)}</li>
                  {quote.applied_rules.map((r, i) => <li key={i}>• {r.label} : +{formatEuro(r.adjustment)}</li>)}
                </ul>
              </details>
            )}

            <div className="mt-6 rounded-xl bg-[#F6F9FC] p-4 text-sm text-slate-600">
              <div className="font-semibold text-[#16324F] mb-1">Serrurier</div>
              <div>{tenant.business_name}</div>
              {tenant.phone && !isDemo && <div>Tél. {tenant.phone}</div>}
              {tenant.email && !isDemo && <div>{tenant.email}</div>}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="cq-download">
                <Button variant="outline" className="rounded-full h-12 border-2 w-full sm:w-auto" style={{ borderColor: primary, color: primary }}><Download className="w-4 h-4 mr-1.5"/> Télécharger mon devis</Button>
              </a>
              <Button
                onClick={() => setShowBooking(true)}
                className="rounded-full h-12 px-8 text-base font-semibold flex-1"
                style={{ background: primary, color: "#fff" }}
                data-testid="cq-book-cta"
              >
                Réserver cette intervention
              </Button>
            </div>
          </section>
        )}

        {showBooking && !alreadyBooked && (
          <section className="mt-8" data-testid="booking-section">
            {isDemo && (
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-dashed px-3 py-1 text-[11px] font-semibold" style={{ borderColor: `${primary}55`, color: primary }} data-testid="demo-env-booking">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }}></span> Environnement de démonstration
              </div>
            )}
            <h2 className="font-display text-2xl font-bold" style={{color: primary}}>Choisissez votre créneau</h2>
            <div className="mt-4 grid gap-4">
              {Object.keys(slotsByDate).length === 0 && (
                <div className="text-slate-500 text-sm">Chargement des créneaux…</div>
              )}
              {Object.entries(slotsByDate).slice(0, 7).map(([d, slots]) => (
                <div key={d}>
                  <div className="font-semibold text-sm capitalize" style={{color: primary}}>
                    {new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {slots.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="kf-slot"
                        data-selected={selectedSlot === s}
                        onClick={() => setSelectedSlot(s)}
                        style={selectedSlot === s ? { background: accent, borderColor: accent, color: "#fff" } : {}}
                        data-testid={`cq-slot-${s}`}
                      >
                        {formatTime(s)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 kf-card p-6">
              <h3 className="font-display text-xl font-bold" style={{color: primary}}>Vos coordonnées</h3>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="grid gap-1.5"><Label>Prénom</Label><Input value={customer.first_name} onChange={(e) => setCustomer({ ...customer, first_name: e.target.value })} className="h-11 rounded-xl" data-testid="cq-first"/></div>
                <div className="grid gap-1.5"><Label>Nom</Label><Input value={customer.last_name} onChange={(e) => setCustomer({ ...customer, last_name: e.target.value })} className="h-11 rounded-xl" data-testid="cq-last"/></div>
                <div className="grid gap-1.5"><Label>Téléphone</Label><Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="h-11 rounded-xl" data-testid="cq-phone"/></div>
                <div className="grid gap-1.5"><Label>Email</Label><Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="h-11 rounded-xl" data-testid="cq-email"/></div>
              </div>
              <p className="mt-3 text-xs text-slate-500" data-testid="cq-privacy-note">
                Vos coordonnées sont transmises au professionnel pour traiter votre demande.{" "}
                <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" style={{ color: primary }}>Confidentialité</a>.
              </p>
            </div>

            <Button
              onClick={submitBooking}
              disabled={submitting || !selectedSlot}
              className="mt-6 rounded-full h-12 px-8 font-semibold w-full"
              style={{ background: primary, color: "#fff" }}
              data-testid="cq-confirm-booking"
            >
              {submitting ? "Réservation…" : "Confirmer la réservation"}
            </Button>
          </section>
        )}

        {/* Answers recap - always visible for context */}
        {(quote.answers && Object.keys(quote.answers).length > 0) && (
          <details className="mt-8 text-sm text-slate-600">
            <summary className="cursor-pointer font-semibold text-[#16324F]">Détails de votre demande</summary>
            <div className="mt-3 grid grid-cols-1 gap-1 pl-2">
              {Object.entries(quote.answers).map(([k, v]) => (
                <div key={k}><span className="text-slate-500">{ANSWER_LABELS[k] || k}</span> : <span className="font-semibold">{ANSWER_VAL[v] || v}</span></div>
              ))}
            </div>
          </details>
        )}
      </main>
    </div>
  );
}
